import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import { debounce } from 'lodash';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  DEVICE_LISTING,
  LISTING_TABLE_FIELDS,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROUTES_MODULES_KEY,
  SORT_ORDER,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  mapAntTableChangeToParams,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import { DELETE_ASSET, NOTIFY_USER } from './graphql/Mutations';
import { ASSETS } from './graphql/Queries';

const buildListingFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  searchString: params?.search,
  filterAsset: params?.filterAsset,
  sortOrder: SORT_ORDER?.[0]?.value,
});

function Listing() {
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: LISTING_TABLE_FIELDS,
  });
  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.ASSETS_LISTING,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const isUpdatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.ASSETS_LISTING,
    allowedPermissions: [PERMISSION_OBJECT?.update],
  });
  const isDeletePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.ASSETS_LISTING,
    allowedPermissions: [PERMISSION_OBJECT?.delete],
  });

  const [executeQuery, { loading, data }] = useLazyQuery(ASSETS, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const [notifyAssetBookedUser] = useMutation(NOTIFY_USER, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const [deleteAsset] = useMutation(DELETE_ASSET, {
    fetchPolicy: 'network-only',
    onCompleted: () => {
      executeQuery({ variables: { filters: buildListingFilter(params) } });
    },
    onError: () => {},
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildListingFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onChange = (pagination, _filters, sorter) => {
    setParams(mapAntTableChangeToParams(pagination, sorter, params?.pageSize));
  };

  const handleSearch = debounce((value) => {
    setParams({ search: value?.trim(), page: 1 });
  }, 500);

  const handleNotifyUser = (deviceId) => {
    notifyAssetBookedUser({
      variables: {
        id: deviceId,
      },
    });
  };

  const handleNotification = (deviceId) => {
    Modal?.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Notify Employee',
      content:
        'A push notification will be sent to the employee who has booked this device; thereby giving a gentle reminder to submit the device back.',
      centered: true,
      className: 'custom-modal-button',
      onOk: () => {
        handleNotifyUser(deviceId);
      },
    });
  };
  const handleDeleteAsset = (record) => {
    Modal?.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Delete Asset',
      content: 'Are you sure to delete this Asset?',
      centered: true,
      className: 'custom-modal-button',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        deleteAsset({
          variables: {
            id: record?.id,
          },
        });
      },
    });
  };

  const handleChange = (value) => {
    setParams({ filterAsset: value?.trim(), page: 1 });
  };

  const columns = [
    {
      title: 'Sr.No',
      key: 'srNo',
      align: 'center',
      width: 50,
      render: (_, __, index) => (
        <>{(params?.page - 1) * params?.pageSize + index + 1}</>
      ),
    },
    {
      title: 'Asset ID',
      dataIndex: 'assetId',
      key: 'assetId',
      align: 'center',
    },
    {
      title: 'Asset Name',
      dataIndex: 'assetName',
      key: 'assetName',
      align: 'center',
    },
    {
      title: 'Type',
      dataIndex: 'assetType',
      key: 'assetType',
      align: 'center',
    },
    {
      title: 'OS',
      dataIndex: 'os',
      key: 'os',
      align: 'center',
    },
    {
      title: 'Version',
      dataIndex: 'osVersion',
      key: 'osVersion',
      align: 'center',
    },
    {
      title: 'Man Soft.',
      dataIndex: 'manufacturerSoftware',
      key: 'manufacturerSoftware',
      align: 'center',
    },
    {
      title: 'Man Soft. V',
      dataIndex: 'manufacturerSoftwareVersion',
      key: 'manufacturerSoftwareVersion',
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      className: 'link-custom-width',
      render: (_, record) => (
        <Tag
          className="status-tags"
          color={record?.isActive ? 'success' : 'error'}
        >
          {record?.isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Availability',
      dataIndex: 'availability',
      key: 'availability',
      align: 'center',
      className: 'link-custom-width',
      render: (_, record) => (
        <>
          <Tag
            className="availability-tags"
            color={record?.availability ? 'success' : 'error'}
          >
            {record?.availability ? 'Yes' : 'No'}
          </Tag>
        </>
      ),
    },
    ...(isUpdatePermission
      ? [
          {
            title: 'Notify Employee',
            dataIndex: 'notify',
            key: 'notify',
            align: 'center',
            render: (_, record) => (
              <>
                {!record?.availability &&
                  record?.isActive &&
                  isUpdatePermission && (
                    <Button
                      key="notification"
                      className={
                        record?.preAssigned
                          ? 'pre-assigned-btn'
                          : 'notify-user-btn'
                      }
                      type="primary"
                      icon={<NotificationOutlined />}
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleNotification(record?.id);
                      }}
                    >
                      <span>{record?.assetTracker?.user?.displayName}</span>
                    </Button>
                  )}
              </>
            ),
          },
        ]
      : []),
    ...(isUpdatePermission || isDeletePermission
      ? [
          {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            render: (_, record) => (
              <Space size="middle">
                {isUpdatePermission && (
                  <Tooltip placement="bottom" title={<span>Edit Asset</span>}>
                    <Button
                      type="text"
                      onClick={(e) => {
                        e?.stopPropagation();
                        history?.push({
                          pathname: `${ROUTES_MODULES_KEY?.ASSETS_LISTING}/edit/${record?.id}`,
                        });
                      }}
                    >
                      <EditOutlined />
                    </Button>
                  </Tooltip>
                )}
                {isDeletePermission && (
                  <Tooltip placement="bottom" title={<span>Delete Asset</span>}>
                    <Button
                      danger
                      type="text"
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleDeleteAsset(record);
                      }}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Tooltip>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Mobile Assets and Management Tracker"
        extra={
          <>
            <div className="listing-filter">
              {isCreatePermission && (
                <Button
                  key="1"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    history?.push({
                      pathname: `${ROUTES_MODULES_KEY?.ASSETS_LISTING}/add`,
                    });
                  }}
                >
                  Add New Device
                </Button>
              )}
              <Select
                value={params?.filterAsset}
                options={DEVICE_LISTING}
                onChange={handleChange}
                showSearch
              />
              <SearchComponent
                key={params?.search || ''}
                getData={handleSearch}
                name="input text"
                defaultValue={params?.search}
              />
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            className="listing-table"
            columns={columns}
            dataSource={data?.assets?.assets}
            loadingData={loading}
            rowKey={(obj) => obj?.id}
            bordered
            onRow={(record) => ({
              onClick: () =>
                isUpdatePermission
                  ? history?.push({
                      pathname: `${ROUTES_MODULES_KEY?.ASSETS_LISTING}/edit/${record?.id}`,
                    })
                  : undefined,
            })}
            rowClassName={() => (isUpdatePermission ? 'pointer' : '')}
            onChange={onChange}
            paginationConfig={{
              current: params?.page,
              total: data?.assets?.count,
              pageSize: params?.pageSize,
            }}
          />
        </div>
      </Card>
    </div>
  );
}

export default Listing;
